/**
 * Catálogo de plantillas iniciales para arrancar un email.
 *
 * Cada starter es un HTML email-safe completo (tabla wrapper + tabla interior
 * + secciones). El usuario las elige en un modal de "Comenzar desde…" y el
 * editor las carga vía `setComponents()`.
 *
 * Convención: ancho fijo 600px, fuente Arial/sans-serif, paleta tailwind.
 * Los merge tags están pre-puestos donde tiene sentido (saludo, firma, footer).
 */

export interface StarterTemplate {
  id: string;
  name: string;
  description: string;
  /** Categoría visible en el filtro del modal. */
  category: 'BLANK' | 'TRANSACTIONAL' | 'MARKETING' | 'OUTREACH';
  html: string;
}

const SHELL_OPEN = `
  <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="width:100%;background:#f3f4f6;">
    <tr><td align="center" style="padding:32px 16px;">
      <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="width:600px;max-width:600px;background:#ffffff;border-radius:8px;overflow:hidden;">
`;

const SHELL_CLOSE = `
      </table>
    </td></tr>
  </table>
`;

const FOOTER = `
  <tr><td align="center" style="padding:24px;background:#f9fafb;font-family:Arial,sans-serif;font-size:12px;color:#6b7280;line-height:1.5;">
    <p style="margin:0 0 4px;">© {{business.year}} {{business.name}}</p>
    <p style="margin:0 0 8px;">{{business.address}}</p>
    <p style="margin:0;">
      <a href="{{system.unsubscribeUrl}}" style="color:#2563eb;text-decoration:underline;">Desuscribirme</a>
    </p>
  </td></tr>
`;

export const STARTER_TEMPLATES: StarterTemplate[] = [
  {
    id: 'blank',
    name: 'En blanco',
    description: 'Lienzo vacío para armarlo desde cero.',
    category: 'BLANK',
    html: '',
  },

  {
    id: 'welcome',
    name: 'Bienvenida',
    description: 'Email post-registro con CTA y próximos pasos.',
    category: 'TRANSACTIONAL',
    html: `
${SHELL_OPEN}
      <tr><td style="padding:48px 32px 24px;font-family:Arial,sans-serif;">
        <h1 style="margin:0 0 12px;font-size:26px;color:#111827;">¡Bienvenido {{contact.firstName}}! 👋</h1>
        <p style="margin:0;font-size:16px;color:#4b5563;line-height:1.6;">
          Nos alegra tenerte en {{business.name}}. Te dejamos los próximos pasos para que aproveches al máximo la plataforma.
        </p>
      </td></tr>
      <tr><td style="padding:0 32px 24px;font-family:Arial,sans-serif;">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="width:100%;">
          <tr><td style="padding:12px 0;border-bottom:1px solid #e5e7eb;">
            <strong style="color:#111827;">1.</strong> <span style="color:#374151;">Completá tu perfil</span>
          </td></tr>
          <tr><td style="padding:12px 0;border-bottom:1px solid #e5e7eb;">
            <strong style="color:#111827;">2.</strong> <span style="color:#374151;">Invitá a tu equipo</span>
          </td></tr>
          <tr><td style="padding:12px 0;">
            <strong style="color:#111827;">3.</strong> <span style="color:#374151;">Configurá tu primera campaña</span>
          </td></tr>
        </table>
      </td></tr>
      <tr><td align="center" style="padding:0 32px 40px;">
        <a href="https://" style="display:inline-block;padding:14px 32px;background:#2563eb;color:#ffffff;font-family:Arial,sans-serif;font-size:14px;text-decoration:none;border-radius:6px;font-weight:600;">
          Empezar ahora
        </a>
      </td></tr>
${FOOTER}
${SHELL_CLOSE}
    `.trim(),
  },

  {
    id: 'newsletter',
    name: 'Newsletter',
    description: 'Tres artículos con imagen + CTA general.',
    category: 'MARKETING',
    html: `
${SHELL_OPEN}
      <tr><td style="padding:32px 32px 16px;font-family:Arial,sans-serif;text-align:center;">
        <p style="margin:0;font-size:12px;color:#6b7280;text-transform:uppercase;letter-spacing:1px;">Newsletter · {{business.name}}</p>
        <h1 style="margin:8px 0 0;font-size:24px;color:#111827;">Lo mejor del mes</h1>
      </td></tr>

      <tr><td style="padding:24px 32px;font-family:Arial,sans-serif;">
        <img src="https://via.placeholder.com/536x280" alt="" style="width:100%;height:auto;border-radius:6px;display:block;" />
        <h2 style="margin:16px 0 8px;font-size:20px;color:#111827;">Artículo 1: título atractivo</h2>
        <p style="margin:0 0 12px;font-size:14px;color:#4b5563;line-height:1.6;">
          Breve introducción al artículo. Captura el interés del lector en pocas líneas.
        </p>
        <a href="https://" style="font-size:13px;color:#2563eb;text-decoration:underline;">Leer más →</a>
      </td></tr>

      <tr><td style="padding:0 32px 24px;">
        <hr style="border:0;border-top:1px solid #e5e7eb;margin:0;" />
      </td></tr>

      <tr><td style="padding:0 32px 24px;font-family:Arial,sans-serif;">
        <h2 style="margin:0 0 8px;font-size:20px;color:#111827;">Artículo 2: novedad de producto</h2>
        <p style="margin:0 0 12px;font-size:14px;color:#4b5563;line-height:1.6;">
          Contale al lector qué cambió y por qué le va a importar.
        </p>
        <a href="https://" style="font-size:13px;color:#2563eb;text-decoration:underline;">Conocer más →</a>
      </td></tr>

      <tr><td style="padding:0 32px 24px;">
        <hr style="border:0;border-top:1px solid #e5e7eb;margin:0;" />
      </td></tr>

      <tr><td style="padding:0 32px 32px;font-family:Arial,sans-serif;">
        <h2 style="margin:0 0 8px;font-size:20px;color:#111827;">Artículo 3: tip rápido</h2>
        <p style="margin:0 0 12px;font-size:14px;color:#4b5563;line-height:1.6;">
          Cierre con valor accionable. Algo que pueda implementar hoy.
        </p>
        <a href="https://" style="font-size:13px;color:#2563eb;text-decoration:underline;">Ver el tip →</a>
      </td></tr>

${FOOTER}
${SHELL_CLOSE}
    `.trim(),
  },

  {
    id: 'promo',
    name: 'Promoción',
    description: 'Hero con descuento + producto destacado + urgencia.',
    category: 'MARKETING',
    html: `
${SHELL_OPEN}
      <tr><td align="center" style="padding:48px 32px;background:linear-gradient(135deg,#7c3aed 0%,#2563eb 100%);font-family:Arial,sans-serif;">
        <p style="margin:0 0 8px;font-size:12px;color:#e9d5ff;text-transform:uppercase;letter-spacing:2px;">Oferta especial</p>
        <h1 style="margin:0 0 12px;font-size:36px;color:#ffffff;line-height:1.2;">30% OFF</h1>
        <p style="margin:0;font-size:16px;color:#ede9fe;line-height:1.5;">
          {{contact.firstName}}, solo por esta semana en {{business.name}}.
        </p>
      </td></tr>

      <tr><td style="padding:40px 32px 16px;font-family:Arial,sans-serif;text-align:center;">
        <p style="margin:0;font-size:15px;color:#374151;line-height:1.6;">
          Aprovechá nuestros mejores productos con un descuento exclusivo para vos.
        </p>
      </td></tr>

      <tr><td style="padding:0 32px 24px;">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="width:100%;background:#ffffff;border:1px solid #e5e7eb;border-radius:8px;">
          <tr><td style="padding:20px;">
            <img src="https://via.placeholder.com/516x240" alt="" style="width:100%;height:auto;border-radius:6px;display:block;" />
            <h2 style="margin:16px 0 8px;font-size:20px;color:#111827;font-family:Arial,sans-serif;">Producto estrella</h2>
            <p style="margin:0 0 12px;font-size:14px;color:#6b7280;font-family:Arial,sans-serif;line-height:1.5;">
              Lo más vendido del último trimestre. Ahora con descuento.
            </p>
            <p style="margin:0;font-family:Arial,sans-serif;">
              <span style="text-decoration:line-through;color:#9ca3af;font-size:14px;">$ 99</span>
              <strong style="color:#2563eb;font-size:22px;margin-left:8px;">$ 69</strong>
            </p>
          </td></tr>
        </table>
      </td></tr>

      <tr><td align="center" style="padding:8px 32px 16px;">
        <a href="https://" style="display:inline-block;padding:14px 36px;background:#2563eb;color:#ffffff;font-family:Arial,sans-serif;font-size:15px;text-decoration:none;border-radius:6px;font-weight:600;">
          Comprar ahora
        </a>
      </td></tr>

      <tr><td align="center" style="padding:0 32px 40px;font-family:Arial,sans-serif;font-size:12px;color:#dc2626;">
        ⏰ La oferta termina el domingo a las 23:59 hs.
      </td></tr>

${FOOTER}
${SHELL_CLOSE}
    `.trim(),
  },

  {
    id: 'reactivation',
    name: 'Reactivación',
    description: 'Re-engagement con beneficios y CTA suave.',
    category: 'OUTREACH',
    html: `
${SHELL_OPEN}
      <tr><td align="center" style="padding:48px 32px 16px;font-family:Arial,sans-serif;">
        <span style="font-size:48px;">👋</span>
        <h1 style="margin:16px 0 12px;font-size:24px;color:#111827;">Te extrañamos, {{contact.firstName}}</h1>
        <p style="margin:0;font-size:15px;color:#4b5563;line-height:1.6;">
          Hace tiempo que no te vemos por {{business.name}}. Queríamos contarte qué hay de nuevo.
        </p>
      </td></tr>

      <tr><td style="padding:24px 32px;font-family:Arial,sans-serif;">
        <table role="presentation" cellpadding="0" cellspacing="0" border="0" style="width:100%;">
          <tr><td style="padding:8px 0;">✅ <span style="color:#374151;">Nuevas funciones que pediste</span></td></tr>
          <tr><td style="padding:8px 0;">✅ <span style="color:#374151;">Soporte 24/7 incluido</span></td></tr>
          <tr><td style="padding:8px 0;">✅ <span style="color:#374151;">Sin cargos sorpresa</span></td></tr>
        </table>
      </td></tr>

      <tr><td align="center" style="padding:16px 32px 40px;">
        <a href="https://" style="display:inline-block;padding:14px 28px;background:#0d9488;color:#ffffff;font-family:Arial,sans-serif;font-size:14px;text-decoration:none;border-radius:6px;font-weight:600;">
          Volver a la app
        </a>
        <p style="margin:16px 0 0;font-family:Arial,sans-serif;font-size:12px;color:#9ca3af;">
          O <a href="{{system.unsubscribeUrl}}" style="color:#9ca3af;text-decoration:underline;">desuscribite</a> si ya no querés saber de nosotros.
        </p>
      </td></tr>

${FOOTER}
${SHELL_CLOSE}
    `.trim(),
  },

  {
    id: 'reminder',
    name: 'Recordatorio',
    description: 'Email simple: aviso + acción + firma.',
    category: 'TRANSACTIONAL',
    html: `
${SHELL_OPEN}
      <tr><td style="padding:48px 32px 16px;font-family:Arial,sans-serif;">
        <h1 style="margin:0 0 12px;font-size:22px;color:#111827;">Hola {{contact.firstName}},</h1>
        <p style="margin:0 0 16px;font-size:15px;color:#374151;line-height:1.6;">
          Te escribo para recordarte que [acción pendiente]. Tenés tiempo hasta [fecha] para resolverlo.
        </p>
        <p style="margin:0;font-size:15px;color:#374151;line-height:1.6;">
          Si ya lo hiciste, ignorá este correo.
        </p>
      </td></tr>

      <tr><td align="center" style="padding:24px 32px;">
        <a href="https://" style="display:inline-block;padding:12px 24px;background:#2563eb;color:#ffffff;font-family:Arial,sans-serif;font-size:14px;text-decoration:none;border-radius:6px;font-weight:600;">
          Resolver ahora
        </a>
      </td></tr>

      <tr><td style="padding:24px 32px 40px;font-family:Arial,sans-serif;font-size:14px;color:#374151;line-height:1.6;">
        <p style="margin:0 0 4px;">Saludos,</p>
        <p style="margin:0;font-weight:600;color:#111827;">{{user.fullName}}</p>
        <p style="margin:0;font-size:13px;color:#6b7280;">{{user.position}} · {{business.name}}</p>
      </td></tr>

${FOOTER}
${SHELL_CLOSE}
    `.trim(),
  },
];

export function getStarterCategoryLabel(c: StarterTemplate['category']): string {
  return {
    BLANK: 'En blanco',
    TRANSACTIONAL: 'Transaccional',
    MARKETING: 'Marketing',
    OUTREACH: 'Re-engagement',
  }[c];
}
